import styled from 'styled-components';
import PulseLoader from 'react-spinners/PulseLoader';
import LaunchIcon from '@mui/icons-material/Launch';
import Markdown from '../Markdown';

import { memo, useState } from 'react';
import { Alert, alpha, Avatar, Box, Chip, CircularProgress, Collapse, Paper, Stack } from '@mui/material';
import { MessageType } from '@common/hooks/useChatbot';
import { useTheme } from '../../../../styles/theme';
import { AutoAwesomeMotionOutlined, BuildOutlined, ExpandMoreOutlined } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { State } from '@store/index';
import { Tooltip } from '@common/Tooltip';
import { MessageToolApproval } from './MessageToolApproval';

const Document = styled.a`
  padding-top: 0.5rem;
  color: ${({ theme }) => theme.text.subtext};
  font-size: 1.6ch;

  &:hover {
    color: ${({ theme }) => theme.primary};
  }

  &:last-of-type {
    border-bottom: 0;
  }

  svg {
    font-size: 1.8ch;
    color: ${({ theme }) => theme.primary};
  }

  .linkWrapper {
    max-width: 300px;

    p {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  }
`;

export const MessageUser = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 24px 60px 1fr 34px;
  padding: 1.5rem 0;
  line-height: 1.5rem;

  p {
    white-space: pre-wrap;
    margin-bottom: 0.75rem;
  }

  img {
    max-width: 100%;
  }

  p:last-of-type {
    margin-bottom: 0;
  }

  pre,
  code {
    white-space: pre-wrap !important;
    word-break: break-word !important;
  }

  .content {
    font-size: 14px;
    display: flex;
    justify-content: center;
    flex-direction: column;
    color: ${({ theme }) => theme.text.standard};
  }

  .writing {
    display: flex;
    align-items: center;

    svg {
      color: black;
      height: 7px;
    }
  }

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: black;
    height: 30px;
    width: 30px;
    font-size: 1.2ch;
    font-weight: 500;
    border-radius: 0.3rem;
    color: white;
  }
`;

export const MessageBot = styled.div`
  width: 100%;
  background-color: ${({ theme }) => alpha(theme.background.inverse, 0.02)};
  border-top: 1px solid ${({ theme }) => theme.line.light};
  border-bottom: 1px solid ${({ theme }) => theme.line.light};
  grid-template-columns: 24px 60px 1fr 34px;
  display: grid;
  padding: 1.5rem 0;
  line-height: 1.5rem;

  p {
    white-space: pre-wrap;
    margin-bottom: 0.75rem;
  }

  img {
    max-width: 100%;
  }

  p:last-of-type {
    margin-bottom: 0;
  }

  a {
    color: ${({ theme }) => theme.primary};
  }

  pre,
  code {
    white-space: pre-wrap !important;
    word-break: break-word !important;
  }

  .content {
    font-size: 14px;
    display: flex;
    justify-content: center;
    flex-direction: column;
    color: ${({ theme }) => theme.text.standard};
  }

  .writing {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.75rem;
    color: ${({ theme }) => theme.text.standard};
  }

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: black;
    height: 30px;
    width: 30px;
    font-size: 1.2ch;
    font-weight: 500;
    border-radius: 0.3rem;
    color: white;
  }

  .metadata {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    border-top: 1px solid ${({ theme }) => theme.line.light};
    justify-content: center;
    margin-top: 1rem;
    padding-top: 0.5rem;
  }

  .tools {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 0.75rem;

    #tool-icon {
      font-size: 14px;
    }
  }

  #node_id {
    color: ${({ theme }) => theme.text.subtext};
    font-size: 12px;
  }
`;

export const Error = styled.div`
  display: grid;
  grid-template-columns: 24px 10fr 24px;
  padding: 2rem 0;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.line.light};
  border-bottom: 1px solid ${({ theme }) => theme.line.light};
  background-color: rgba(0, 0, 0, 0.01);
`;

const WorkspaceIcon = styled.div`
  background-color: ${({ theme }) => theme.primary};
  height: 30px;
  width: 30px;
  border-radius: 0.3rem;
  display: flex;
  margin-right: 1rem;
  align-items: center;
  justify-content: center;

  svg {
    height: 18px;
  }
`;

function extractThinkContent(message: string): { think: string | null; main: string } {
  try {
    const openTag = '<think>';
    const closeTag = '</think>';
    const openIndex = message.indexOf(openTag);
    if (openIndex !== -1) {
      const closeIndex = message.indexOf(closeTag, openIndex + openTag.length);
      if (closeIndex === -1) {
        return { think: message.replace(openTag, '').trim(), main: '' };
      } else {
        const thinkContent = message
          .slice(0, closeIndex + closeTag.length)
          .replace(openTag, '')
          .replace(closeTag, '')
          .trim();
        const mainMessage = message.slice(closeIndex + closeTag.length).trim();
        return { think: thinkContent, main: mainMessage };
      }
    }
    return { think: null, main: message };
  } catch (error) {
    return { think: null, main: message };
  }
}

interface Props {
  entry: MessageType;
  setExpandModalOpen?: React.Dispatch<React.SetStateAction<string | null>>;
}

export const Message = memo(({ entry, setExpandModalOpen }: Props) => {
  const { message, type, state, node_id } = entry;

  const [expanded, setExpanded] = useState(false);

  const aborted = useSelector((state: State) => state.tabs.active.run.aborted);
  const themeType = useSelector((state: State) => state.flow.theme);
  const name = useSelector((state: State) => state.auth.name);
  const theme = useTheme();

  const hasDocuments = entry?.events?.documents?.length > 0;
  const hasFunctions = entry?.events?.functions?.length > 0;

  const isStillThinking = typeof message === 'string' && message.includes('<think>') && !message.includes('</think>');
  const { think, main: mainMessage } = extractThinkContent(message);

  const toolChips = !(hasFunctions && setExpandModalOpen) ? null : (
    <div className='tools'>
      {entry.events.functions.map((tool, index) => (
        <Tooltip key={index} title='Open Tool' placement='top' arrow>
          <Chip
            label={tool.action}
            icon={
              <Box pr={0.5} pt={0.25} display='flex' alignItems='center'>
                {tool.output ? (
                  <BuildOutlined color='inherit' id='tool-icon' />
                ) : (
                  <CircularProgress size={12} color='inherit' />
                )}
              </Box>
            }
            onClick={() => setExpandModalOpen(tool.id)}
          />
        </Tooltip>
      ))}
    </div>
  );

  const nameInitials =
    name
      ?.split(' ')
      .map((n) => n[0])
      .join('') || 'U';

  if (type === 'bot' && state === 'error') {
    return (
      <Error>
        <div />
        <div className='content'>
          <Alert severity='error'>
            <Markdown content={message} />
          </Alert>
        </div>
        <div />
      </Error>
    );
  }

  if (type === 'bot') {
    return (
      <MessageBot>
        <div />
        <Stack spacing={0.5} display='flex' alignItems='flex-start'>
          <WorkspaceIcon>
            <AutoAwesomeMotionOutlined sx={{ color: themeType === 'light' ? '#ffffff' : '#000000' }} />
          </WorkspaceIcon>
          <p id='node_id'>ID: {node_id}</p>
        </Stack>
        {state === 'loading' && !aborted && (
          <div className='writing'>
            {toolChips}
            <PulseLoader size={6} color={theme.text.standard} speedMultiplier={0.8} />
          </div>
        )}
        {aborted && (
          <div className='content'>
            <Alert severity='warning'>The request was aborted.</Alert>
          </div>
        )}

        {(state === 'complete' || state === 'streaming') && !aborted && (
          <div className='content'>
            {toolChips}
            {think && (
              <div className='think' style={{ marginBottom: '1rem' }}>
                <Chip
                  onClick={() => setExpanded(!expanded)}
                  label={
                    <Stack direction='row' alignItems='center' spacing={1}>
                      <span>Reasoning</span>
                      {isStillThinking && <CircularProgress size={12} color='inherit' />}
                      <ExpandMoreOutlined
                        sx={{
                          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s',
                        }}
                      />
                    </Stack>
                  }
                  sx={{ cursor: 'pointer' }}
                />
                <Collapse in={expanded}>
                  <Paper
                    variant='outlined'
                    sx={{
                      borderRadius: '16px',
                      backgroundColor: theme.background.light,
                      padding: '1rem',
                      marginTop: '0.5rem',
                    }}
                  >
                    <Markdown content={think} />
                  </Paper>
                </Collapse>
              </div>
            )}
            {mainMessage && <Markdown content={mainMessage} />}
            {state === 'complete' && hasDocuments && (
              <div className='metadata'>
                {entry.events.documents.map((document, index) => (
                  <Document key={index} href={document.url} target='_blank'>
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <LaunchIcon />
                      <div className='linkWrapper'>
                        <p>{document.title}</p>
                      </div>
                    </Stack>
                  </Document>
                ))}
              </div>
            )}
            <Collapse in={state === 'complete'}>
              <MessageToolApproval entry={entry} />
            </Collapse>
          </div>
        )}
      </MessageBot>
    );
  }

  return (
    <MessageUser>
      <div />
      <Avatar
        sx={{
          border: `1px solid ${theme.line.light}`,
          color: theme.text.standard,
          backgroundColor: theme.badge.background,
          width: 30,
          height: 30,
          borderRadius: '5px',
          fontSize: '13px',
          fontWeight: 500,
          fontFamily: 'Inter',
        }}
      >
        {nameInitials}
      </Avatar>
      <div className='content'>
        <Markdown content={message} />
      </div>
    </MessageUser>
  );
});

Message.displayName = 'Message';
